"use client";

import {
	CheckIcon,
	ChevronDownIcon,
	Loader2Icon,
	MinusIcon,
	PencilIcon,
	PlusIcon,
	SparklesIcon,
	XIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../../lib";

const IDLE_BUTTON_CLASS =
	"cursor-not-allowed border-transparent text-muted-foreground/40";

const CANCEL_ACTIVE_CLASS =
	"cursor-pointer border-border bg-background text-muted-foreground hover:bg-muted";

const CONFIRM_ACTIVE_CLASS =
	"cursor-pointer border-primary bg-primary text-primary-foreground hover:bg-primary/90";

export const EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS =
	"inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border transition-colors";

/** @deprecated Prefer {@link EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS}. */
export const FIELD_CONFIRM_BUTTON_BASE_CLASS =
	EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS;

export interface EditableFieldConfirmActionsProps {
	isDirty: boolean;
	disabled?: boolean;
	isSaving?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	confirmAriaLabel?: string;
	cancelAriaLabel?: string;
	className?: string;
	/** When true, Cancel stays enabled even if the field is clean (e.g. exit edit mode). */
	allowCancelWhenClean?: boolean;
}

/**
 * Cancel (X) + Confirm (Check) — shared chrome for editable fields and
 * confirm-mode textareas (jobs dialog, etc.).
 */
export function EditableFieldConfirmActions({
	isDirty,
	disabled = false,
	isSaving = false,
	onConfirm,
	onCancel,
	confirmAriaLabel = "Confirm changes",
	cancelAriaLabel = "Cancel changes",
	className,
	allowCancelWhenClean = false,
}: EditableFieldConfirmActionsProps) {
	const confirmInactive = !isDirty || disabled || isSaving;
	const cancelInactive =
		disabled || isSaving || (!isDirty && !allowCancelWhenClean);
	return (
		<div className={cn("flex items-center gap-1", className)}>
			<button
				type="button"
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					!cancelInactive ? CANCEL_ACTIVE_CLASS : IDLE_BUTTON_CLASS,
				)}
				disabled={cancelInactive}
				aria-label={cancelAriaLabel}
				onClick={onCancel}
			>
				<XIcon className="size-4" aria-hidden />
			</button>
			<button
				type="button"
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					!confirmInactive && isDirty
						? CONFIRM_ACTIVE_CLASS
						: IDLE_BUTTON_CLASS,
				)}
				disabled={confirmInactive}
				aria-label={confirmAriaLabel}
				onClick={onConfirm}
			>
				{isSaving ? (
					<Loader2Icon className="size-4 animate-spin" aria-hidden />
				) : (
					<CheckIcon className="size-4" aria-hidden />
				)}
			</button>
		</div>
	);
}

export interface EditableFieldPencilButtonProps {
	onClick: () => void;
	disabled?: boolean;
	ariaLabel?: string;
	"data-testid"?: string;
	className?: string;
}

export function EditableFieldPencilButton({
	onClick,
	disabled = false,
	ariaLabel = "Edit",
	"data-testid": dataTestId,
	className,
}: EditableFieldPencilButtonProps) {
	return (
		<button
			type="button"
			className={cn(
				EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
				"border-border bg-background text-muted-foreground hover:bg-muted",
				disabled &&
					"cursor-not-allowed border-transparent text-muted-foreground/40",
				className,
			)}
			disabled={disabled}
			aria-label={ariaLabel}
			data-testid={dataTestId}
			onClick={onClick}
		>
			<PencilIcon className="size-4" aria-hidden />
		</button>
	);
}

export interface EditableFieldRowStepperProps {
	onRemove?: () => void;
	onAdd?: () => void;
	removeDisabled?: boolean;
	addDisabled?: boolean;
	removeAriaLabel?: string;
	addAriaLabel?: string;
	className?: string;
}

/** Minus / Plus using the same size-8 confirm/cancel button language. */
export function EditableFieldRowStepper({
	onRemove,
	onAdd,
	removeDisabled = false,
	addDisabled = false,
	removeAriaLabel = "Remove row",
	addAriaLabel = "Add row",
	className,
}: EditableFieldRowStepperProps) {
	return (
		<div className={cn("flex items-center gap-1", className)}>
			<button
				type="button"
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					removeDisabled ? IDLE_BUTTON_CLASS : CANCEL_ACTIVE_CLASS,
				)}
				disabled={removeDisabled}
				aria-label={removeAriaLabel}
				onClick={onRemove}
			>
				<MinusIcon className="size-4" aria-hidden />
			</button>
			<button
				type="button"
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					addDisabled ? IDLE_BUTTON_CLASS : CONFIRM_ACTIVE_CLASS,
				)}
				disabled={addDisabled}
				aria-label={addAriaLabel}
				onClick={onAdd}
			>
				<PlusIcon className="size-4" aria-hidden />
			</button>
		</div>
	);
}

/** Shared segment chrome inside {@link EditableFieldImproveSplitButton}. */
export const EDITABLE_FIELD_IMPROVE_SPLIT_SEGMENT_CLASS =
	"inline-flex shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:text-muted-foreground/40";

export interface EditableFieldImproveSplitButtonProps {
	onPrimaryClick: () => void;
	/** Caret opens guided instruction UI — parent owns popover. */
	onCaretClick?: () => void;
	disabled?: boolean;
	pending?: boolean;
	primaryAriaLabel?: string;
	caretAriaLabel?: string;
	"data-testid"?: string;
	className?: string;
	/** `default` = size-8 sparkles + narrow caret; `sm` = size-7 (composer). */
	size?: "default" | "sm";
	/** Optional trailing node (e.g. Popover trigger wrapping the caret segment). */
	caretSlot?: ReactNode;
}

/**
 * Sparkles primary + caret — one continuous block with a vertical separator.
 * Parent wires caret to instruction popover when needed.
 */
export function EditableFieldImproveSplitButton({
	onPrimaryClick,
	onCaretClick,
	disabled = false,
	pending = false,
	primaryAriaLabel = "Improve",
	caretAriaLabel = "Improve with instructions",
	"data-testid": dataTestId,
	className,
	size = "default",
	caretSlot,
}: EditableFieldImproveSplitButtonProps) {
	const inactive = disabled || pending;
	const isSm = size === "sm";
	const iconClass = isSm ? "size-3.5" : "size-4";
	return (
		<div
			className={cn(
				"inline-flex items-stretch overflow-hidden rounded-md border bg-background",
				isSm ? "h-7" : "h-8",
				inactive ? "border-transparent" : "border-border",
				className,
			)}
			data-testid={dataTestId}
		>
			<button
				type="button"
				className={cn(
					EDITABLE_FIELD_IMPROVE_SPLIT_SEGMENT_CLASS,
					isSm ? "size-7" : "size-8",
					inactive && "cursor-not-allowed hover:bg-transparent",
				)}
				disabled={inactive}
				aria-label={primaryAriaLabel}
				data-testid={
					dataTestId
						? `${dataTestId}-primary`
						: "improve-field-primary"
				}
				onClick={onPrimaryClick}
			>
				{pending ? (
					<Loader2Icon
						className={cn(iconClass, "animate-spin")}
						aria-hidden
					/>
				) : (
					<SparklesIcon className={iconClass} aria-hidden />
				)}
			</button>
			<div
				className={cn(
					"w-px shrink-0 self-stretch",
					inactive ? "bg-border/40" : "bg-border",
				)}
				aria-hidden
			/>
			{caretSlot ?? (
				<button
					type="button"
					className={cn(
						EDITABLE_FIELD_IMPROVE_SPLIT_SEGMENT_CLASS,
						isSm ? "h-7 w-5" : "h-8 w-6",
						inactive && "cursor-not-allowed hover:bg-transparent",
					)}
					disabled={inactive}
					aria-label={caretAriaLabel}
					data-testid={
						dataTestId
							? `${dataTestId}-caret`
							: "improve-field-caret"
					}
					onClick={onCaretClick}
				>
					<ChevronDownIcon className="size-3.5" aria-hidden />
				</button>
			)}
		</div>
	);
}
